import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { devicesApi } from '../../services/api';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

interface Device {
  id: string;
  imei: string;
  name: string | null;
  deviceType: 'LOAN' | 'SUBSCRIPTION';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  vehiclePlate: string | null;
  isOffline: boolean;
  isOverdue: boolean;
  isInRepo: boolean;
  isAggressive: boolean;
}

export function DevicesPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [flagFilter, setFlagFilter] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['devices', { limit: 100 }],
    queryFn: async () => {
      const response = await devicesApi.list({ limit: 100 });
      // All queries under the ['devices', ...] key MUST return Device[] —
      // pages sharing the key receive each other's cached data as-is.
      const list = response.data.data;
      return (Array.isArray(list) ? list : list?.devices || []) as Device[];
    },
  });

  const devices = data ?? [];

  // Filter devices
  const filteredDevices = devices.filter((device) => {
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      const matchesSearch =
        device.imei.toLowerCase().includes(searchLower) ||
        (device.name?.toLowerCase().includes(searchLower) ?? false) ||
        (device.vehiclePlate?.toLowerCase().includes(searchLower) ?? false);
      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter && device.status !== statusFilter) return false;

    // Type filter
    if (typeFilter && device.deviceType !== typeFilter) return false;

    // Flag filter
    if (flagFilter) {
      switch (flagFilter) {
        case 'offline':
          if (!device.isOffline) return false;
          break;
        case 'overdue':
          if (!device.isOverdue) return false;
          break;
        case 'repo':
          if (!device.isInRepo) return false;
          break;
        case 'aggressive':
          if (!device.isAggressive) return false;
          break;
      }
    }

    return true;
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dispositivos</h1>
          <p className="text-gray-600">Gestióna los dispositivos GPS del sistema</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <div className="flex flex-wrap gap-4">
          <div className="w-64">
            <Input
              placeholder="Buscar por IMEI, nombre o placa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filtrar por estado"
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos los estados</option>
            <option value="ACTIVE">Activo</option>
            <option value="INACTIVE">Inactivo</option>
            <option value="SUSPENDED">Suspendido</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Filtrar por tipo de dispositivo"
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos los tipos</option>
            <option value="LOAN">Préstamo</option>
            <option value="SUBSCRIPTION">Suscripción</option>
          </select>
          <select
            value={flagFilter}
            onChange={(e) => setFlagFilter(e.target.value)}
            aria-label="Filtrar por marca (offline, atrasado, incaute, agresivo)"
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos los flags</option>
            <option value="offline">Offline</option>
            <option value="overdue">Atrasado</option>
            <option value="repo">En Incaute</option>
            <option value="aggressive">Agresivo</option>
          </select>
          {(search || statusFilter || typeFilter || flagFilter) && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearch('');
                setStatusFilter('');
                setTypeFilter('');
                setFlagFilter('');
              }}
            >
              Limpiar filtros
            </Button>
          )}
        </div>
        <div className="mt-2 text-sm text-gray-500">
          Mostrando {filteredDevices.length} de {devices.length} dispositivos
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Dispositivo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  IMEI
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Vehículo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Flags
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDevices.map((device) => (
                <tr key={device.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link
                      to={`/devices/${device.id}`}
                      className="font-medium text-blue-600 hover:text-blue-800"
                    >
                      {device.name || device.imei}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {device.imei}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        device.deviceType === 'LOAN'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {device.deviceType === 'LOAN' ? 'Préstamo' : 'Suscripción'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        device.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800'
                          : device.status === 'SUSPENDED'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {device.status === 'ACTIVE'
                        ? 'Activo'
                        : device.status === 'SUSPENDED'
                        ? 'Suspendido'
                        : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {device.vehiclePlate || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1">
                      {device.isOffline && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                          Offline
                        </span>
                      )}
                      {device.isOverdue && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                          Atrasado
                        </span>
                      )}
                      {device.isInRepo && (
                        <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                          Incaute
                        </span>
                      )}
                      {device.isAggressive && (
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                          Agresivo
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <Link
                        to={`/devices/${device.id}`}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Ver detalle
                      </Link>
                      <Link
                        to={`/history?device=${device.id}`}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Historial
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredDevices.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-gray-500">
                    No se encontraron dispositivos
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
